(() => {
  'use strict';

  const STORAGE_KEY = 'mp:wishlist:v1';
  const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';

  const read = () => {
    try {
      const value = JSON.parse(localStorage.getItem(STORAGE_KEY) || '[]');
      return Array.isArray(value) ? [...new Set(value.filter(item => typeof item === 'string' && item))] : [];
    } catch (error) {
      return [];
    }
  };

  const write = handles => {
    try { localStorage.setItem(STORAGE_KEY, JSON.stringify(handles)); } catch (error) { /* storage unavailable */ }
    document.dispatchEvent(new CustomEvent('mp:wishlist:change', { detail: { handles } }));
  };

  const contains = handle => read().includes(handle);

  const updateControls = () => {
    const handles = read();
    document.querySelectorAll('[data-wishlist-toggle]').forEach(button => {
      const active = handles.includes(button.dataset.productHandle);
      const productTitle = button.getAttribute('aria-label')?.split(': ').slice(1).join(': ') || '';
      const label = active ? 'Von der Wunschliste entfernen' : 'Zur Wunschliste hinzufügen';
      button.classList.toggle('is-active', active);
      button.setAttribute('aria-pressed', String(active));
      button.setAttribute('aria-label', productTitle ? `${label}: ${productTitle}` : label);
      button.title = label;
      const labelNode = button.querySelector('[data-wishlist-label]');
      if (labelNode) labelNode.textContent = label;
    });

    document.querySelectorAll('[data-wishlist-count]').forEach(counter => {
      counter.textContent = String(handles.length);
      counter.classList.toggle('hidden', handles.length === 0);
    });
  };

  const toggle = handle => {
    const handles = read();
    const index = handles.indexOf(handle);
    if (index === -1) handles.unshift(handle);
    else handles.splice(index, 1);
    write(handles);
    updateControls();
    return index === -1;
  };

  const currentVariant = button => {
    if (button.dataset.wishlistPlacement !== 'product') return null;
    const json = button.closest('main-product')?.querySelector('[data-selected-variant]')?.textContent;
    if (!json) return null;
    try { return JSON.parse(json); } catch (error) { return null; }
  };

  const publishAnalytics = (added, button) => {
    if (!window.Shopify?.analytics?.publish) return;
    const variant = currentVariant(button);
    const priceCents = Number(variant?.price ?? button.dataset.price ?? 0);
    const item = {
      item_id: String(button.dataset.productId || ''),
      item_name: button.dataset.productTitle || '',
      item_variant: variant?.title || button.dataset.variantTitle || '',
      price: priceCents / 100,
      quantity: 1,
      shopify_product_id: String(button.dataset.productId || ''),
      shopify_variant_id: String(variant?.id || button.dataset.variantId || ''),
      wishlist_placement: button.dataset.wishlistPlacement || 'unknown'
    };
    const customData = {
      currency: button.dataset.currency || 'EUR',
      value: priceCents / 100,
      items: [item]
    };
    const eventName = added
      ? 'mp_wishlist:add_to_wishlist'
      : 'mp_wishlist:remove_from_wishlist';
    window.Shopify.analytics.publish(eventName, customData);
  };

  const money = (cents, locale, currency) => {
    try {
      return new Intl.NumberFormat(locale || 'de-AT', {
        style: 'currency', currency: currency || 'EUR'
      }).format(Number(cents || 0) / 100);
    } catch (error) {
      return `${(Number(cents || 0) / 100).toFixed(2).replace('.', ',')} €`;
    }
  };

  const escapeHtml = value => String(value ?? '').replace(/[&<>'"]/g, char => ({
    '&': '&amp;', '<': '&lt;', '>': '&gt;', "'": '&#39;', '"': '&quot;'
  })[char]);

  const productUrl = handle => `${root}products/${encodeURIComponent(handle)}`;

  async function fetchProduct(handle) {
    const response = await fetch(`${productUrl(handle)}.js`, { headers: { Accept: 'application/json' } });
    if (!response.ok) throw new Error(String(response.status));
    return response.json();
  }

  function cardMarkup(product, locale, currency) {
    const availableVariant = product.variants.find(variant => variant.available);
    const image = product.featured_image || (product.images && product.images[0]) || '';
    const url = productUrl(product.handle);
    const availabilityClass = availableVariant ? '' : ' mp-wishlist-card__unavailable';
    const cartButton = availableVariant
      ? `<button type="button" class="button button--solid" data-wishlist-add-cart data-variant-id="${availableVariant.id}">In den Warenkorb</button>`
      : '<span class="button button--outline" aria-disabled="true">Ausverkauft</span>';

    return `<article class="mp-wishlist-card${availabilityClass}" data-wishlist-card="${escapeHtml(product.handle)}">
      <a class="mp-wishlist-card__media" href="${url}">
        ${image ? `<img src="${escapeHtml(image)}&width=900" alt="${escapeHtml(product.title)}" loading="lazy" width="675" height="900">` : ''}
      </a>
      <button type="button" class="mp-wishlist-button mp-wishlist-card__remove is-active" data-wishlist-toggle data-product-handle="${escapeHtml(product.handle)}" data-product-id="${product.id}" data-product-title="${escapeHtml(product.title)}" data-variant-id="${availableVariant?.id || product.variants[0]?.id || ''}" data-variant-title="${escapeHtml(availableVariant?.title || product.variants[0]?.title || '')}" data-price="${product.price}" data-currency="${escapeHtml(currency)}" data-wishlist-placement="wishlist_page" aria-pressed="true" aria-label="Von der Wunschliste entfernen: ${escapeHtml(product.title)}" title="Von der Wunschliste entfernen">
        <svg aria-hidden="true" focusable="false" viewBox="0 0 24 24" fill="none"><path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78L12 21.23l8.84-8.84a5.5 5.5 0 0 0 0-7.78Z"/></svg>
      </button>
      <div class="mp-wishlist-card__meta">
        <h2 class="mp-wishlist-card__title h6"><a href="${url}">${escapeHtml(product.title)}</a></h2>
        <div class="mp-wishlist-card__price p3">${money(product.price, locale, currency)}</div>
        <div class="mp-wishlist-card__actions">
          
          <a class="button button--outline" href="${url}">Zum Produkt</a>
        </div>
      </div>
    </article>`;
  }

  async function renderPage(page) {
    const grid = page.querySelector('[data-wishlist-grid]');
    const empty = page.querySelector('[data-wishlist-empty]');
    const loading = page.querySelector('[data-wishlist-loading]');
    if (!grid || !empty || !loading) return;

    const handles = read();
    if (!handles.length) {
      grid.innerHTML = '';
      loading.hidden = true;
      empty.hidden = false;
      return;
    }

    loading.hidden = false;
    empty.hidden = true;
    const results = await Promise.allSettled(handles.map(fetchProduct));
    const products = [];
    const validHandles = [];
    results.forEach((result, index) => {
      if (result.status === 'fulfilled') {
        products.push(result.value);
        validHandles.push(handles[index]);
      }
    });
    if (validHandles.length !== handles.length) write(validHandles);
    grid.innerHTML = products.map(product => cardMarkup(product, page.dataset.locale, page.dataset.currency)).join('');
    loading.hidden = true;
    empty.hidden = products.length > 0;
    updateControls();
  }

  document.addEventListener('click', async event => {
    const toggleButton = event.target.closest('[data-wishlist-toggle]');
    if (toggleButton) {
      event.preventDefault();
      event.stopPropagation();
      const handle = toggleButton.dataset.productHandle;
      if (!handle) return;
      const added = toggle(handle);
      publishAnalytics(added, toggleButton);
      const card = toggleButton.closest('[data-wishlist-card]');
      if (card && !contains(handle)) card.remove();
      const page = document.querySelector('[data-wishlist-page]');
      if (page && !read().length) renderPage(page);
      return;
    }

    const cartButton = event.target.closest('[data-wishlist-add-cart]');
    if (!cartButton) return;
    cartButton.disabled = true;
    cartButton.setAttribute('aria-busy', 'true');
    try {
      const response = await fetch(window.routes?.cart_add_url || `${root}cart/add.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items: [{ id: Number(cartButton.dataset.variantId), quantity: 1 }] })
      });
      if (!response.ok) throw new Error('cart');
      window.location.href = window.routes?.cart_url || `${root}cart`;
    } catch (error) {
      cartButton.disabled = false;
      cartButton.removeAttribute('aria-busy');
      cartButton.textContent = 'Bitte erneut versuchen';
    }
  });

  const init = () => {
    updateControls();
    const page = document.querySelector('[data-wishlist-page]');
    if (page) renderPage(page);
  };

  document.addEventListener('mp:wishlist:change', updateControls);
  document.addEventListener('shopify:section:load', init);
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();
})();
